/*
 * Copyright (c) 2025 AirAsia Berhad (Malaysia). All rights reserved.
 *
 * This software is proprietary and confidential. Unauthorized copying of this file,
 * via any medium, is strictly prohibited. Do not distribute, copy, modify, or reuse
 * any part of this code without explicit written permission from AirAsia Move.
 *
 * For internal use only.
 */

import { useCallback } from 'react';
import { NativeModules, Platform } from 'react-native';
import { format } from 'date-fns';
import { useQueryClient } from '@tanstack/react-query';
import {
  DEFAULT_CURRENCY,
  DEFAULT_LANGUAGE,
  openInAppBrowser,
  useAppConfig,
} from '@airasia-rn/ui';
import { useCurrency } from '@airasia-rn/ui/config';
import { useSuperAppContext } from '@airasia-rn/ui/context';
import { useHotelConfig } from '../configs/HotelConfigProvider';
import {
  FILTER_STATE_KEY,
  HOTEL,
  HOTEL_SEARCH_DEFAULTS,
  MAX_VALUE,
  MIN_VALUE,
  MOVE_HOTEL_LIST_PAGE_BASE_URLS,
  OLD_HOTEL_LIST_PAGE_BASE_URLS,
  SORT_BY,
} from '../configs/constants';
import { useHandleHotelListNavigation } from './useHandleHotelListNavigation';
import {
  useOptionalHotelsNavigation,
  useOptionalHotelsRoutes,
  useHotelsSearchParams,
} from '../context/HotelsNavigationContext';
import {
  buildHotelListUrl,
  formatOccupancy,
  getCurrentWindowURLParams,
  parseHotelSearchParams,
} from '../utils/deeplink';
import type {
  HotelFilters,
  HotelRecentSearch,
  HotelSearchData,
} from '../types';
import { storeSearchParams } from '../utils/hotelSearchStorage';
import { preserveHotelSearchAttribution } from '../utils/hotelListSearchUrl';
import { clearFeaturedDestinationOrigin } from './useFeaturedDestinationOrigin';
import {
  RECENT_SEARCHES_QUERY_KEY,
  flattenHotelRecentSearchesForHistory,
  useRecentSearches,
} from './useRecentSearch';
import {
  removeEmptySelectedHotelQueryParams,
  syncSelectedHotelFilter,
} from '../utils/selectedHotelFilter';

type UseHandleHotelSearchOptions = {
  source?: 'hotels' | 'home';
};

type QueryParamValue = string | string[] | undefined;
type QueryParams = Record<string, QueryParamValue>;

const buildNewWebHotelListParams = ({
  currency,
  countryCode,
  destinationId,
  checkIn,
  checkOut,
  languageId,
  occupancy,
  userSelectedHotelId,
  userSelectedHotelName,
}: {
  currency: string;
  countryCode: string;
  destinationId: string;
  checkIn: Date;
  checkOut: Date;
  languageId: string;
  occupancy: string;
  userSelectedHotelId?: string;
  userSelectedHotelName?: string;
}) => {
  const params: Record<string, string> = {
    currency: currency || HOTEL_SEARCH_DEFAULTS.CURRENCY,
    country_code: countryCode || HOTEL_SEARCH_DEFAULTS.COUNTRY_CODE,
    checkin: format(checkIn, 'yyyy-MM-dd'),
    checkout: format(checkOut, 'yyyy-MM-dd'),
    occupancy: occupancy || HOTEL_SEARCH_DEFAULTS.OCCUPANCY,
    location_id: destinationId || HOTEL_SEARCH_DEFAULTS.LOCATION_ID,
    languageId: languageId || DEFAULT_LANGUAGE,
  };

  if (userSelectedHotelId) {
    params.aaId = userSelectedHotelId;
    if (userSelectedHotelName) {
      params.destination_name = userSelectedHotelName;
    }
  }

  return params;
};

const getQueryParamValue = (
  params: QueryParams,
  ...keys: string[]
): string | undefined => {
  for (const key of keys) {
    const value = params[key];
    if (typeof value === 'string' && value.length > 0) {
      return value;
    }

    if (Array.isArray(value)) {
      const firstValue = value.find((item) => item.length > 0);
      if (firstValue) {
        return firstValue;
      }
    }
  }

  return undefined;
};

const hasCoreSearchCriteria = (params: QueryParams) =>
  Boolean(
    getQueryParamValue(params, 'location_id', 'destination', 'locationId'),
  ) &&
  Boolean(getQueryParamValue(params, 'checkin', 'fromDate')) &&
  Boolean(getQueryParamValue(params, 'checkout', 'toDate'));

const normalizeSignatureValue = (value?: string) => (value || '').trim();
const normalizeSignatureLowerValue = (value?: string) =>
  normalizeSignatureValue(value).toLowerCase();
const normalizeSignatureUpperValue = (value?: string) =>
  normalizeSignatureValue(value).toUpperCase();

const buildNewWebHotelSearchSignature = (params: {
  currency?: string;
  country_code?: string;
  checkin?: string;
  checkout?: string;
  occupancy?: string;
  location_id?: string;
  language?: string;
  languageId?: string;
  aaId?: string;
  user_selected_hotel_id?: string;
  destination_name?: string;
}) =>
  [
    normalizeSignatureValue(params.location_id),
    normalizeSignatureValue(params.checkin),
    normalizeSignatureValue(params.checkout),
    normalizeSignatureValue(params.occupancy),
    normalizeSignatureUpperValue(params.currency),
    normalizeSignatureUpperValue(params.country_code),
    normalizeSignatureLowerValue(params.language || params.languageId),
    normalizeSignatureValue(params.aaId || params.user_selected_hotel_id),
    normalizeSignatureLowerValue(params.destination_name),
  ].join('|');

const isCurrentNewWebListingPath = () => {
  if (typeof window === 'undefined') {
    return false;
  }

  const pathname = window.location?.pathname || '';
  const normalizedPathname = pathname.replace(/\/+$/, '') || '/';

  return (
    normalizedPathname === '/' ||
    normalizedPathname === '/search' ||
    normalizedPathname.endsWith('/hotel') ||
    normalizedPathname.endsWith('/hotel/search') ||
    normalizedPathname.endsWith('/v2/hotels/search')
  );
};

const isSameCurrentNewWebHotelSearch = (
  nextParams: ReturnType<typeof buildNewWebHotelListParams>,
) => {
  if (typeof window === 'undefined' || !isCurrentNewWebListingPath()) {
    return {
      isSame: false,
      reason: 'not_web_or_not_new_listing_path',
      currentSignature: '',
      nextSignature: '',
      currentWindowParams: null as ReturnType<
        typeof getCurrentWindowURLParams
      > | null,
    };
  }

  const currentWindowParams = getCurrentWindowURLParams();
  if (!hasCoreSearchCriteria(currentWindowParams)) {
    return {
      isSame: false,
      reason: 'missing_core_search_criteria',
      currentSignature: '',
      nextSignature: '',
      currentWindowParams,
    };
  }

  const currentSearchParams = parseHotelSearchParams(currentWindowParams);
  const currentSignature = buildNewWebHotelSearchSignature({
    currency:
      getQueryParamValue(currentWindowParams, 'currency') ||
      HOTEL_SEARCH_DEFAULTS.CURRENCY,
    country_code:
      getQueryParamValue(currentWindowParams, 'country_code', 'countryCode') ||
      HOTEL_SEARCH_DEFAULTS.COUNTRY_CODE,
    checkin: getQueryParamValue(currentWindowParams, 'checkin', 'fromDate'),
    checkout: getQueryParamValue(currentWindowParams, 'checkout', 'toDate'),
    occupancy:
      getQueryParamValue(currentWindowParams, 'occupancy') ||
      currentSearchParams.occupancy,
    location_id: getQueryParamValue(
      currentWindowParams,
      'location_id',
      'destination',
      'locationId',
    ),
    language:
      getQueryParamValue(currentWindowParams, 'languageId', 'language') ||
      DEFAULT_LANGUAGE,
    aaId: getQueryParamValue(
      currentWindowParams,
      'aaId',
      'user_selected_hotel_id',
      'user_selected_id',
    ),
    destination_name: getQueryParamValue(
      currentWindowParams,
      'destination_name',
    ),
  });
  const nextSignature = buildNewWebHotelSearchSignature(nextParams);

  return {
    isSame: currentSignature === nextSignature,
    reason: 'compared_signatures',
    currentSignature,
    nextSignature,
    currentWindowParams,
  };
};

const getCurrentHotelListQueryParams = (routeParams: QueryParams) => {
  if (Platform.OS === 'web' && typeof window !== 'undefined') {
    return getCurrentWindowURLParams();
  }

  return routeParams;
};

const getCurrentLocationId = (params: QueryParams) =>
  getQueryParamValue(params, 'location_id', 'destination', 'locationId') || '';

const getCurrentSelectedHotelId = (params: QueryParams) =>
  getQueryParamValue(
    params,
    'aaId',
    'user_selected_hotel_id',
    'user_selected_id',
  ) || '';

const isSameLocationSearch = (
  currentLocationId: string,
  nextLocationId: string,
) => {
  const trimmedCurrentLocationId = currentLocationId.trim();
  const trimmedNextLocationId = nextLocationId.trim();

  return (
    trimmedCurrentLocationId.length > 0 &&
    trimmedNextLocationId.length > 0 &&
    trimmedCurrentLocationId === trimmedNextLocationId
  );
};

const getDefaultSearchFilters = (): HotelFilters => ({
  priceRange: [MIN_VALUE, MAX_VALUE],
  selectedPriceRanges: [],
  sortBy: SORT_BY.DEFAULT,
  selectedPromos: [],
  guestRating: '',
  selectedHotelId: '',
  selectedHotelName: '',
  starRating: [],
  selectedAmenities: [],
  selectedRateAmenities: [],
  selectedPropertyTypes: [],
  selectedPaymentPolicies: [],
  selectedLandmark: undefined,
});

/**
 * Hook for handling hotel search redirect based on feature flag.
 *
 * When the feature flag is disabled:
 *   - Redirects to the existing (legacy) hotel listing page.
 *
 * When the feature flag is enabled:
 *   - Redirects to the new MOVE hotel listing page.
 *
 * Both flows retain the user's search criteria in the redirect URL
 * and work for both guest and logged-in flows.
 *
 * Note: China domain check is already handled in SearchFormView
 * before onSubmit is called, so it is not duplicated here.
 */
export const useHandleHotelSearch = (options?: UseHandleHotelSearchOptions) => {
  const { config: appConfig } = useAppConfig();
  const { currency } = useCurrency();
  const { geoId: countryCodeFromContext } = useSuperAppContext();
  const { config: hotelConfig } = useHotelConfig();
  const navigation = useOptionalHotelsNavigation();
  const routes = useOptionalHotelsRoutes();
  const routeParams = useHotelsSearchParams();
  const queryClient = useQueryClient();
  const { addRecentSearch } = useRecentSearches();
  const source = options?.source ?? hotelConfig?.searchSource ?? 'hotels';
  const {
    navigateToHotelListUrl,
    getHotelListRoutePath,
    shouldUseNewWebListing,
    shouldUseMoveListForSource,
  } = useHandleHotelListNavigation({ source });
  const handleSearch = useCallback(
    (searchData: HotelSearchData) => {
      if (
        !searchData.destination ||
        !searchData.checkIn ||
        !searchData.checkOut
      ) {
        return;
      }

      const checkin = format(searchData.checkIn, 'yyyy-MM-dd');
      const checkout = format(searchData.checkOut, 'yyyy-MM-dd');

      addRecentSearch(
        searchData.destination,
        checkin,
        checkout,
        searchData.rooms,
      );

      if (
        Platform.OS !== 'web' &&
        NativeModules?.ReactToNative?.sendSearchHistory
      ) {
        const updatedRecentSearches = queryClient.getQueryData<
          HotelRecentSearch[]
        >(RECENT_SEARCHES_QUERY_KEY);
        const searchHistoryEntries = flattenHotelRecentSearchesForHistory(
          updatedRecentSearches,
        );

        if (searchHistoryEntries.length) {
          NativeModules.ReactToNative.sendSearchHistory(
            'hotel',
            searchHistoryEntries,
          );
        }
      }

      // A real search supersedes any featured-destination provenance, including a
      // search for the very destination that was tapped.
      clearFeaturedDestinationOrigin(queryClient);

      const resolvedCurrency = currency || DEFAULT_CURRENCY;
      const resolvedCountryCode =
        hotelConfig?.geoId ?? appConfig.geoId ?? countryCodeFromContext ?? 'MY';
      const resolvedLanguageId = appConfig.locale || DEFAULT_LANGUAGE;
      const moveBaseUrl =
        appConfig.env === 'prd'
          ? MOVE_HOTEL_LIST_PAGE_BASE_URLS.prod
          : MOVE_HOTEL_LIST_PAGE_BASE_URLS.stg;
      const legacyBaseUrl =
        appConfig.env === 'prd'
          ? OLD_HOTEL_LIST_PAGE_BASE_URLS.prod
          : OLD_HOTEL_LIST_PAGE_BASE_URLS.stg;
      const occupancy = formatOccupancy(searchData.rooms);
      const currentQueryParams = getCurrentHotelListQueryParams(
        routeParams as QueryParams,
      );
      const cachedFilters =
        queryClient.getQueryData<HotelFilters>(FILTER_STATE_KEY);
      // Some recent-search and prefilled suggestions carry the selected hotel
      // ID even when their type metadata is missing or inconsistent. Treat the
      // ID as authoritative so a previous same-location selection cannot win.
      const submittedSelectedHotelId =
        searchData.destination.type?.toLowerCase() === HOTEL ||
        Boolean(searchData.destination.aa_id?.trim())
          ? searchData.destination.aa_id?.trim() || ''
          : '';
      const previousSelectedHotelId =
        getCurrentSelectedHotelId(currentQueryParams);
      const shouldPreservePreviousSelectedHotel =
        !submittedSelectedHotelId &&
        searchData.destinationChanged !== true &&
        isSameLocationSearch(
          getCurrentLocationId(currentQueryParams),
          searchData.destination.id || '',
        ) &&
        previousSelectedHotelId.length > 0;
      const selectedHotelId =
        submittedSelectedHotelId ||
        (shouldPreservePreviousSelectedHotel ? previousSelectedHotelId : '');
      const selectedPropertyFilterName = submittedSelectedHotelId
        ? searchData.destination.name || ''
        : shouldPreservePreviousSelectedHotel
          ? cachedFilters?.selectedHotelName || ''
          : '';
      const destinationForListing = {
        ...searchData.destination,
        aa_id: selectedHotelId || '',
        type: selectedHotelId ? HOTEL : searchData.destination.type,
      };

      // Store search parameters for persistence when coming back
      if (Platform.OS === 'web') {
        syncSelectedHotelFilter(
          queryClient,
          selectedHotelId || '',
          selectedPropertyFilterName,
        );

        storeSearchParams({
          location_id: searchData.destination.id,
          checkin: format(searchData.checkIn, 'yyyy-MM-dd'),
          checkout: format(searchData.checkOut, 'yyyy-MM-dd'),
          currency: resolvedCurrency,
          country_code: resolvedCountryCode,
          language: resolvedLanguageId,
          occupancy,
          // A location label can always be recovered later from location_id via
          // smart fill, so HOTEL-29674 drops it once the destination changes. A
          // hotel's name cannot: smart fill resolves the location id and returns
          // the surrounding area instead. Dropping it there left the trip back
          // to relabel the field with the hotel's location, so a hotel keeps its
          // label whether or not the destination changed.
          ...(searchData.destinationChanged !== true || submittedSelectedHotelId
            ? {
                destination_name: searchData.destination.name,
                destination_full_name: searchData.destination.full_name,
                destination_type: searchData.destination.type,
              }
            : {}),
          aaId: selectedHotelId || undefined,
        });
      }

      if (
        Platform.OS !== 'web' &&
        shouldUseMoveListForSource &&
        source !== 'home'
      ) {
        queryClient.setQueryData<HotelFilters>(
          FILTER_STATE_KEY,
          selectedHotelId
            ? {
                ...getDefaultSearchFilters(),
                selectedHotelId,
                selectedHotelName: selectedPropertyFilterName,
              }
            : getDefaultSearchFilters(),
        );
      }

      const buildListPageUrl = (routePath: string) =>
        buildHotelListUrl({
          routePath,
          destination: destinationForListing,
          checkIn: searchData.checkIn!, // Non-null assertion - checked above
          checkOut: searchData.checkOut!, // Non-null assertion - checked above
          rooms: searchData.rooms,
          currency: resolvedCurrency,
          countryCode: resolvedCountryCode,
          languageId: resolvedLanguageId,
          rate_amenity: undefined,
        });

      if (Platform.OS === 'web') {
        if (shouldUseNewWebListing) {
          const listPageParams = buildNewWebHotelListParams({
            currency: resolvedCurrency,
            countryCode: resolvedCountryCode,
            destinationId: destinationForListing.id,
            checkIn: searchData.checkIn,
            checkOut: searchData.checkOut,
            languageId: resolvedLanguageId,
            occupancy,
            userSelectedHotelId: selectedHotelId || undefined,
            userSelectedHotelName: selectedPropertyFilterName || undefined,
          });
          const compareResult = isSameCurrentNewWebHotelSearch(listPageParams);

          if (source !== 'home' && compareResult.isSame) {
            return;
          }

          // Home has no hotels-web routes; always target `/search` so prod lands on
          // `/hotel/search?…` instead of `/hotel?…` (Expo baseUrl drops query).
          const newWebListingRoutePath =
            source === 'home' ? '/search' : getHotelListRoutePath();
          const nextUrl = `${newWebListingRoutePath}?${new URLSearchParams(
            listPageParams,
          ).toString()}`;

          navigateToHotelListUrl(preserveHotelSearchAttribution(nextUrl));
          return;
        }

        const listPageUrl = buildListPageUrl(legacyBaseUrl);
        navigateToHotelListUrl(preserveHotelSearchAttribution(listPageUrl));
        return;
      }

      if (shouldUseMoveListForSource) {
        if (source === 'home') {
          const externalMoveListPageUrl = buildListPageUrl(moveBaseUrl);

          openInAppBrowser(externalMoveListPageUrl);
          return;
        }

        // MOVE listing: Use in-app navigation with buildHotelListUrl
        // This keeps the same URL format with all query params (F_R, occupancy, days, etc.)
        const baseListPageUrl = removeEmptySelectedHotelQueryParams(
          buildListPageUrl(routes?.HOTELS_LIST ?? '/search'),
        );
        // Native initialParams are merged into every results entry. Override
        // the canonical keys too, otherwise old deep-link values win over
        // the new destination/fromDate/toDate aliases when parsed.
        const canonicalSearchParams = new URLSearchParams({
          location_id: destinationForListing.id,
          checkin,
          checkout,
          country_code: resolvedCountryCode,
        });
        const listPageUrl = `${baseListPageUrl}&${canonicalSearchParams.toString()}`;
        // Edit-search from the listing sets from_listing=1. Dismiss directly to
        // the existing list entry in one navigation action. Calling goBack()
        // and then replace() in separate commits can race Fabric's native-view
        // detach/attach work on Android.
        const isFromListing =
          getQueryParamValue(routeParams as QueryParams, 'from_listing') ===
          '1';

        if (isFromListing) {
          if (navigation) {
            if (navigation.canGoBack?.()) {
              navigation.dismissTo(listPageUrl);
            } else {
              navigation.replaceListUrl(listPageUrl);
            }
            return;
          }

          const { router: nativeRouter } = require('expo-router');

          if (nativeRouter.canGoBack?.()) {
            nativeRouter.dismissTo(listPageUrl);
          } else {
            nativeRouter.replace(listPageUrl);
          }
          return;
        }

        if (navigation) {
          navigation.navigateToListUrl(listPageUrl);
        } else {
          const { router: nativeRouter } = require('expo-router');
          nativeRouter.push(listPageUrl);
        }
      } else {
        // Legacy listing: Use external redirect to different domain
        const baseUrl =
          appConfig.env === 'prd'
            ? OLD_HOTEL_LIST_PAGE_BASE_URLS.prod
            : OLD_HOTEL_LIST_PAGE_BASE_URLS.stg;

        const listPageUrl = buildListPageUrl(baseUrl);
        openInAppBrowser(listPageUrl);
      }
    },
    [
      appConfig,
      currency,
      hotelConfig,
      countryCodeFromContext,
      getHotelListRoutePath,
      navigateToHotelListUrl,
      navigation,
      routes,
      shouldUseMoveListForSource,
      shouldUseNewWebListing,
      routeParams,
      source,
      queryClient,
      addRecentSearch,
    ],
  );

  return { handleSearch, isMoveListEnabled: shouldUseMoveListForSource };
};
